import jwtDecode from 'jwt-decode';
import { action, makeObservable, observable } from 'mobx';

import { login, loginAs, permissions, register, session } from '@/api/auth';
import { AUTH_URL } from '@/constants';
import type { AccessTokenProvider } from '@/utils/access-token-provider';
import { settingsStore } from './index';

export class AuthStore {
  @observable loggedIn: boolean = false;
  @observable profile: any = {};
  @observable permissions: any = {};

  constructor(private readonly tokenProvider: AccessTokenProvider) {
    makeObservable(this);
  }

  @action async completeLogin() {
    const token = this.tokenProvider.get();

    if (token) {
      const decoded = jwtDecode(token) as any;
      await this.loadPermissions(token);

      this.profile = decoded?.user;
      this.loggedIn = true;

      await settingsStore.loadSettings();
    }
  }

  @action logout() {
    this.loggedIn = false;
    this.profile = {};
    this.permissions = {};
    this.tokenProvider.remove();
  }

  isAuthorized = () => {
    return this.loggedIn && !!this.tokenProvider.get();
  };

  loadPermissions = async (token: string) => {
    const perms = await permissions(token);
    this.setPermissions(perms);
  };

  @action setPermissions = (perms: Record<string, number>) => {
    this.permissions = {};
    this.permissions = perms;
  };

  hasPermissions = (perms: [string, number][]) => {
    return perms.reduce((final: boolean, [obj, mask]: any) => {
      return final && (this.permissions[obj] & mask) === mask;
    }, true);
  };

  hasAtLeastOnePermission = (perms: [string, number][]) => {
    return perms.reduce((final: boolean, [obj, mask]: any) => {
      return final || (this.permissions[obj] & mask) === mask;
    }, false);
  };

  checkSession = async () => {
    const token = this.tokenProvider.get();
    try {
      if (token) {
        await session(token);
        await this.completeLogin();
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        this.tokenProvider.remove();
      }
    }
  };

  loginWithEmail = async (email: string, password: string) => {
    const response = await login({ email, password });

    const { token } = response.data;
    this.tokenProvider.set(token);

    await this.completeLogin();
  };

  register = async (email: string, password: string, repeatPassword: string) => {
    await register({ email, password, repeatPassword });
  };

  loginWithSaml = async (provider: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const win = window.open(
        `${AUTH_URL}/saml/${provider}/login`,
        'saml-login',
        'height=500,width=500,left=700,top=250',
      );

      if (!win) {
        reject(new Error('Failed to open the sign-in window'));
        return;
      }

      const authOrigin = new URL(AUTH_URL).origin;
      let closeWatcher: number;
      let closeGraceTimer: number;

      const cleanup = () => {
        window.removeEventListener('message', onMessage);
        window.clearInterval(closeWatcher);
        window.clearTimeout(closeGraceTimer);
      };

      const onMessage = async (event: MessageEvent) => {
        if (event.source !== win || event.origin !== authOrigin || typeof event.data !== 'string' || !event.data) {
          return;
        }

        cleanup();
        win.close();

        try {
          this.tokenProvider.set(event.data);
          await this.completeLogin();
          resolve();
        } catch (err) {
          this.tokenProvider.remove();
          reject(err);
        }
      };

      window.addEventListener('message', onMessage);

      closeWatcher = window.setInterval(() => {
        if (win.closed) {
          window.clearInterval(closeWatcher);
          // the token message can still be queued behind the close tick when the popup closes itself after posting
          closeGraceTimer = window.setTimeout(() => {
            cleanup();
            reject(new Error('Sign-in was not completed'));
          }, 300);
        }
      }, 500);
    });
  };

  loginWithRoles = async (roleIds: number[]) => {
    const accessToken = this.tokenProvider.get();
    const response = await loginAs({ roleIds }, accessToken);

    const { token } = response.data;
    this.tokenProvider.set(token);

    await this.completeLogin();
  };
}
